/**
 * Notification WebSocket Service
 *
 * Singleton-сервис для получения real-time уведомлений через WebSocket.
 * Использует SharedWebSocket для координации между вкладками.
 *
 * Эмитирует сырые WS-события (INSERT/UPDATE/DELETE).
 * Логика загрузки/отображения уведомлений — в NotificationContext (Task 6).
 */

import { SharedWebSocket } from '../utils/shared-websocket';
import authService from './auth.service';
import { onAuthTokenRefreshed } from '../utils/authTokenEmitter';
import type { NotificationOut } from '../types/notification.types';

export interface NotificationWsEvent {
  op: 'INSERT' | 'UPDATE' | 'DELETE';
  id: number;
  user_id: number;
  /** Полный объект уведомления, если backend прислал его в `data` — позволяет пропустить REST getById. */
  payload?: NotificationOut;
}

type MessageCallback = (ev: NotificationWsEvent) => void;
type ConnectionCallback = (connected: boolean) => void;
type PollingFallbackCallback = () => void;

const LOG_PREFIX = '[NotifWS]';
const PING_INTERVAL_MS = 30_000;
// Exponential backoff с cap 30s: 1 → 2 → 4 → 8 → 16 → 30 → 30 ...
const BACKOFF_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const MAX_RECONNECT_ATTEMPTS = 8;

function buildUrl(): string {
  const tokens = authService.getTokens();
  const token = tokens?.access_token ?? '';
  // Match statsWebSocket pattern: same host as the page, backend routes /api/v1/ws/* internally.
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v1/ws/notifications?token=${encodeURIComponent(token)}`;
}

class NotificationWebSocketService {
  private sharedWs: SharedWebSocket | null = null;
  private pingIntervalId: ReturnType<typeof setInterval> | null = null;
  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private generation = 0;

  private messageListeners = new Set<MessageCallback>();
  private connectionListeners = new Set<ConnectionCallback>();
  private pollingFallbackListeners = new Set<PollingFallbackCallback>();

  constructor() {
    // Wire JWT refresh handler at module load — not at first connect.
    onAuthTokenRefreshed(() => {
      if (this.sharedWs !== null) {
        console.log(LOG_PREFIX, 'JWT обновлён — пересоздаём соединение');
        this.reconnect();
      }
    });
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  connect(): void {
    if (this.sharedWs !== null) {
      // Already connected / connecting — nothing to do.
      return;
    }
    this.reconnectAttempt = 0;
    this._createSharedWs();
  }

  disconnect(): void {
    this.generation++;
    this._clearPingInterval();
    this._clearReconnectTimer();
    if (this.sharedWs) {
      this.sharedWs.close();
      this.sharedWs = null;
    }
  }

  /**
   * Destroy the current connection and create a new one with a fresh token.
   * Called on JWT refresh and when the consumer wants to force reconnect.
   */
  reconnect(): void {
    this.generation++;
    this._clearPingInterval();
    this._clearReconnectTimer();
    if (this.sharedWs) {
      this.sharedWs.close();
      this.sharedWs = null;
    }
    this.reconnectAttempt = 0;
    this._createSharedWs();
  }

  onMessage(cb: MessageCallback): () => void {
    this.messageListeners.add(cb);
    return () => this.messageListeners.delete(cb);
  }

  onConnectionChange(cb: ConnectionCallback): () => void {
    this.connectionListeners.add(cb);
    return () => this.connectionListeners.delete(cb);
  }

  onPollingFallback(cb: PollingFallbackCallback): () => void {
    this.pollingFallbackListeners.add(cb);
    return () => this.pollingFallbackListeners.delete(cb);
  }

  // ─── Internal helpers ────────────────────────────────────────────────────

  private _createSharedWs(): void {
    const myGen = ++this.generation;
    const url = buildUrl();
    console.log(LOG_PREFIX, 'Подключение к', url.replace(/token=[^&]+/, 'token=***'));

    const ws = new SharedWebSocket(url);
    this.sharedWs = ws;

    ws.onopen = () => {
      if (this.generation !== myGen) return;
      console.log(LOG_PREFIX, 'Соединение установлено');
      this.reconnectAttempt = 0;
      this._notifyConnection(true);
      this._startPingInterval();
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.generation !== myGen) return;
      let msg: unknown;
      try {
        msg = JSON.parse(event.data as string);
      } catch {
        console.warn(LOG_PREFIX, 'Не удалось распарсить сообщение:', event.data);
        return;
      }

      if (typeof msg !== 'object' || msg === null) {
        console.warn(LOG_PREFIX, 'unknown message shape', msg);
        return;
      }

      // Keepalive reply — ignore silently.
      if ((msg as { type?: string }).type === 'pong') {
        return;
      }

      const m = msg as Record<string, unknown>;

      // Legacy shape: {op, id, user_id}
      if ('op' in m && 'id' in m && 'user_id' in m) {
        const ev: NotificationWsEvent = {
          op: m.op as NotificationWsEvent['op'],
          id: m.id as number,
          user_id: m.user_id as number,
        };
        this._notifyMessage(ev);
        return;
      }

      // New shape: {type: 'notification.<action>', data: NotificationOut}
      if (typeof m.type === 'string' && m.type.startsWith('notification.') && m.data && typeof m.data === 'object') {
        const data = m.data as NotificationOut;
        const op = mapTypeToOp(m.type);
        if (op == null) return;
        this._notifyMessage({
          op,
          id: data.id,
          user_id: data.user_id,
          payload: op === 'DELETE' ? undefined : data,
        });
        return;
      }

      console.warn(LOG_PREFIX, 'unknown message shape', msg);
    };

    ws.onclose = (event: CloseEvent) => {
      if (this.generation !== myGen) return; // FIX 1: stale onclose ignored
      console.warn(LOG_PREFIX, 'Соединение закрыто:', { code: event.code, reason: event.reason });
      this._clearPingInterval();
      this._notifyConnection(false);

      // 1008 = policy violation (invalid/expired token) — let AuthContext handle re-login.
      if (event.code === 1008) {
        console.warn(LOG_PREFIX, 'Код 1008 — не переподключаемся, требуется повторный логин');
        return;
      }

      this._scheduleReconnect(myGen);
    };

    ws.onerror = (event: Event) => {
      if (this.generation !== myGen) return;
      console.error(LOG_PREFIX, 'Ошибка соединения:', event);
    };
  }

  private _startPingInterval(): void {
    this._clearPingInterval();
    this.pingIntervalId = setInterval(() => {
      if (this.sharedWs) {
        try {
          this.sharedWs.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // Connection may have dropped; onclose will handle it.
        }
      }
    }, PING_INTERVAL_MS);
  }

  private _clearPingInterval(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  private _scheduleReconnect(gen: number): void {
    this._clearReconnectTimer();

    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      console.warn(LOG_PREFIX, `Превышен лимит переподключений (${MAX_RECONNECT_ATTEMPTS}). Переключаемся на polling.`);
      this.sharedWs = null;
      this._notifyPollingFallback();
      return;
    }

    const delay = BACKOFF_DELAYS_MS[this.reconnectAttempt] ?? BACKOFF_DELAYS_MS[BACKOFF_DELAYS_MS.length - 1];
    this.reconnectAttempt++;
    console.log(LOG_PREFIX, `Переподключение через ${delay}мс (попытка ${this.reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS})`);

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      if (this.generation !== gen) return; // FIX 2: stale timer ignored
      this._createSharedWs();
    }, delay);
  }

  private _clearReconnectTimer(): void {
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }

  private _notifyMessage(ev: NotificationWsEvent): void {
    this.messageListeners.forEach(cb => {
      try {
        cb(ev);
      } catch (err) {
        console.error(LOG_PREFIX, 'Ошибка в message listener:', err);
      }
    });
  }

  private _notifyConnection(connected: boolean): void {
    this.connectionListeners.forEach(cb => {
      try {
        cb(connected);
      } catch (err) {
        console.error(LOG_PREFIX, 'Ошибка в connection listener:', err);
      }
    });
  }

  private _notifyPollingFallback(): void {
    this.pollingFallbackListeners.forEach(cb => {
      try {
        cb();
      } catch (err) {
        console.error(LOG_PREFIX, 'Ошибка в pollingFallback listener:', err);
      }
    });
  }
}

function mapTypeToOp(type: string): NotificationWsEvent['op'] | null {
  switch (type) {
    case 'notification.created':
      return 'INSERT';
    case 'notification.updated':
    case 'notification.read':
      return 'UPDATE';
    case 'notification.deleted':
    case 'notification.dismissed':
      return 'DELETE';
    default:
      return null;
  }
}

const notificationWebSocketService = new NotificationWebSocketService();
export default notificationWebSocketService;
